import type * as XLSXType from "xlsx";
import { get } from "svelte/store";
import type { RiskEntry, Task, WbsNode } from "./types";
import { DEFAULT_WBS } from "./wbs";
import { riskLevel, linkedActivitiesFor } from "./risk";
import { tasks, risks, wbsNodes, twoShifts } from "./stores";
import { downloadFile } from "./download";
import { taskShiftsPerDay } from "./cpm";

type Row = (string | number)[];

function withWidths(ws: XLSXType.WorkSheet, rows: Row[]): XLSXType.WorkSheet {
  const widths: number[] = [];
  rows.forEach((row) => row.forEach((cell, i) => {
    const len = String(cell ?? "").length;
    widths[i] = Math.max(widths[i] ?? 8, Math.min(len + 2, 60));
  }));
  ws["!cols"] = widths.map((w) => ({ wch: w }));
  return ws;
}

/**
 * Optimistic/most-likely/pessimistic are stored as shift-units for a task that runs a
 * double shift (see `Task.doubleShift` in `types.ts`) — dividing each by its own
 * `taskShiftsPerDay` here means this sheet always reads in real calendar days
 * regardless of the project's shift setting or any one activity's own shift coverage,
 * so it's a self-contained input for whatever reads it next (a reviewer, another
 * Monte Carlo tool).
 */
function activitiesSheet(xlsx: typeof XLSXType, tasksIn: Task[], nodes: WbsNode[], projectShiftsPerDay: 1 | 2): XLSXType.WorkSheet {
  const header: Row = [
    "ID", "Name", "Type", "Phase", "WBS code", "WBS name", "Predecessors (IDs)",
    "Optimistic (d)", "Most likely (d)", "Pessimistic (d)", "Linked risk",
    "Required subcontracts", "Responsible party", "Required equipment", "Required personnel",
  ];
  const rows: Row[] = tasksIn.map((t) => {
    const node = nodes.find((n) => n.id === t.wbsId);
    const shiftsPerDay = taskShiftsPerDay(t, projectShiftsPerDay);
    return [
      t.id, t.name, t.type, t.phase, node?.code ?? "", node?.name ?? "", t.preds.join(" "),
      t.o / shiftsPerDay, t.m / shiftsPerDay, t.p / shiftsPerDay, t.risk, t.subcontractors.join(" "), t.responsibleParty,
      t.requiredEquipment, t.requiredPersonnel,
    ];
  });
  return withWidths(xlsx.utils.aoa_to_sheet([header, ...rows]), [header, ...rows]);
}

function risksSheet(xlsx: typeof XLSXType, risksIn: RiskEntry[], tasksIn: Task[]): XLSXType.WorkSheet {
  const header: Row = ["Code", "Description", "Probability", "Impact", "Level", "Owner", "Mitigation", "Status", "Linked activities (IDs)"];
  const rows: Row[] = risksIn.map((r) => [
    r.id, r.desc, r.probability, r.impact, riskLevel(r) ?? "", r.owner, r.mitigation, r.status,
    linkedActivitiesFor(r.id, tasksIn).join(" "),
  ]);
  return withWidths(xlsx.utils.aoa_to_sheet([header, ...rows]), [header, ...rows]);
}

function wbsSheet(xlsx: typeof XLSXType, nodes: WbsNode[]): XLSXType.WorkSheet {
  const header: Row = ["Code", "Name", "Parent code"];
  const rows: Row[] = nodes.map((n) => [n.code, n.name, nodes.find((p) => p.id === n.parentId)?.code ?? ""]);
  return withWidths(xlsx.utils.aoa_to_sheet([header, ...rows]), [header, ...rows]);
}

function readmeSheet(xlsx: typeof XLSXType, shiftsPerDay: 1 | 2): XLSXType.WorkSheet {
  const lines: Row[] = [
    ["Crane changeover planning — export"],
    [""],
    ["Activities feeds the critical-path and Monte Carlo calculation directly:"],
    ["  Optimistic / Most likely / Pessimistic (d) are the O/M/P triangular-distribution inputs per activity, in calendar days."],
    ["  Predecessors (IDs) is a space-separated list of values from the ID column, not activity names."],
    ...(shiftsPerDay === 2
      ? [["  This project runs 2 shifts/day — for an activity with a second team on it (Shift 1 + 2), the duration here has already been converted from the app's own shift-unit input into calendar days."]]
      : []),
    [""],
    ["Keep the ID column unchanged when editing existing rows — it's the stable key predecessors and risk links point to."],
    ["Give a new row its own unused ID rather than reusing or renumbering an existing one."],
    [""],
    ["Exported " + new Date().toISOString().slice(0, 10) + "."],
  ];
  const ws = xlsx.utils.aoa_to_sheet(lines);
  ws["!cols"] = [{ wch: 100 }];
  return ws;
}

export function buildPlanningWorkbook(
  xlsx: typeof XLSXType,
  tasksIn: Task[],
  risksIn: RiskEntry[],
  nodes: WbsNode[] = DEFAULT_WBS,
  shiftsPerDay: 1 | 2 = 1
): XLSXType.WorkBook {
  const wb = xlsx.utils.book_new();
  xlsx.utils.book_append_sheet(wb, readmeSheet(xlsx, shiftsPerDay), "Read me");
  xlsx.utils.book_append_sheet(wb, activitiesSheet(xlsx, tasksIn, nodes, shiftsPerDay), "Activities");
  xlsx.utils.book_append_sheet(wb, risksSheet(xlsx, risksIn, tasksIn), "Risks");
  xlsx.utils.book_append_sheet(wb, wbsSheet(xlsx, nodes), "WBS");
  return wb;
}

/** Loaded on demand — the xlsx library is the single largest dependency in this app. */
export async function exportPlanningWorkbook(): Promise<void> {
  const xlsx = await import("xlsx");
  const wb = buildPlanningWorkbook(xlsx, get(tasks), get(risks), get(wbsNodes), get(twoShifts) ? 2 : 1);
  const out = xlsx.write(wb, { bookType: "xlsx", type: "array" });
  const mime = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
  await downloadFile("crane-changeover-planning.xlsx", new Blob([out], { type: mime }), mime);
}
